import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { characters } from "@/mocks/characters";
import { cpLines } from "@/mocks/cpLines";

interface Result {
  key: string;
  label: string;
  sub: string;
  to: string;
  kind: string;
}

export default function Hero() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [focused, setFocused] = useState(false);

  const results: Result[] = (() => {
    const k = keyword.trim();
    if (!k) return [];
    const chars: Result[] = characters
      .filter((c) => c.name.includes(k) || c.title.includes(k) || c.tags.some((t) => t.includes(k)))
      .map((c) => ({ key: c.id, label: c.name, sub: c.title, to: "/relations", kind: "人物" }));
    const cps: Result[] = cpLines
      .filter((c) => c.name.includes(k) || c.pairing.includes(k))
      .map((c) => ({ key: c.id, label: c.name, sub: c.pairing, to: "/cp-lines", kind: "CP线" }));
    return [...chars, ...cps].slice(0, 6);
  })();

  const submit = () => {
    if (!keyword.trim()) return;
    if (results.length > 0) {
      navigate(results[0].to);
    } else {
      navigate("/relations");
    }
  };

  return (
    <section className="relative flex min-h-[560px] w-full items-center md:min-h-[660px]">
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Grand%20traditional%20Chinese%20cultivation%20sect%20on%20towering%20misty%20mountains%20at%20dawn%2C%20layered%20ink%20wash%20landscape%2C%20ethereal%20clouds%2C%20distant%20pagodas%20and%20flying%20silhouettes%2C%20warm%20vermilion%20and%20gold%20light%20rays%20breaking%20through%2C%20cinematic%20epic%20atmosphere%2C%20refined%20detail&width=1920&height=1080&seq=hero-home-01&orientation=landscape"
          alt="云隐峰修仙宗门水墨晨雾大景"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/55 to-background-950/85"></div>

      <div className="relative mx-auto w-full max-w-7xl px-4 py-28 md:px-6 md:py-36">
        <div className="mx-auto max-w-3xl text-center">
          <p className="inline-flex items-center gap-2 rounded-full border border-accent-300/40 bg-background-950/30 px-4 py-1.5 text-xs tracking-widest text-accent-200 backdrop-blur">
            <i className="ri-sparkling-2-line" />
            追更前先补人物关系课
          </p>
          <h1 className="mt-6 font-heading text-4xl font-bold leading-tight text-background-50 md:text-6xl">
            师兄啊师兄
            <span className="mt-3 block font-[Ma_Shan_Zheng] text-3xl font-normal text-accent-300 md:text-5xl">
              人物关系一页看懂
            </span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-sm leading-8 text-foreground-100/85 md:text-base">
            师兄关系亲疏、师姐互动名场面、同门情谊暗线全梳理，CP 线发展脉络与师门日常细节同步更新。
            新粉入门、老粉考据，都从这里开始。
          </p>

          <form
            className="mx-auto mt-8 flex max-w-xl items-center gap-2 rounded-full border border-background-100/25 bg-background-950/40 p-1.5 backdrop-blur"
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            <i className="ri-search-line pl-3 text-lg text-foreground-100/70" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setTimeout(() => setFocused(false), 150)}
              placeholder="搜索人物、CP 线，如：陆沉渊、沉舟线"
              aria-label="搜索人物与CP线"
              className="w-full bg-transparent py-2 text-sm text-foreground-50 outline-none placeholder:text-foreground-100/50"
            />
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          {focused && results.length > 0 && (
            <div className="mx-auto mt-2 max-w-xl overflow-hidden rounded-xl border border-background-100/20 bg-background-950/90 text-left backdrop-blur">
              {results.map((r) => (
                <Link
                  key={r.key}
                  to={r.to}
                  className="flex items-center justify-between px-4 py-3 transition-colors hover:bg-background-800/50"
                >
                  <div>
                    <p className="text-sm text-foreground-50">{r.label}</p>
                    <p className="text-xs text-foreground-100/60">{r.sub}</p>
                  </div>
                  <span className="rounded-full bg-secondary-500/20 px-3 py-1 text-xs text-secondary-300">
                    {r.kind}
                  </span>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/relations"
              className="whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              查看人物关系图
            </Link>
            <Link
              to="/cp-lines"
              className="whitespace-nowrap rounded-md border border-background-100/30 px-7 py-3 text-sm text-foreground-50 transition-colors hover:bg-background-50/10"
            >
              浏览 CP 线脉络
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-1/2 hidden -translate-x-1/2 pb-6 text-accent-200/80 md:block">
        <i className="ri-arrow-down-line animate-bounce text-2xl" />
      </div>
    </section>
  );
}